import styles from './styles.module.css'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import Error from '@/components/ui/error'
import { AddIcon, CloseIcon } from '@/components/ui/icons'

export const UPLOAD_TYPES = {
  BIG: 'big',
  SMALL: 'small',
}

const Upload = ({
  label,
  removeLabel,
  name,
  setFieldValue,
  setFieldError,
  onBlur,
  onRemove,
  value,
  errorMessage,
  options,
  type,
}) => {
  const { t } = useTranslation(['validation'])
  const fileRef = useRef()
  const [preview, setPreview] = useState(null)
  const [errorText, setErrorText] = useState(null)

  useEffect(() => {
    if (typeof errorMessage === 'string' && errorMessage.startsWith('validation::')) {
      setErrorText(t(errorMessage))
      return
    }
    setErrorText(errorMessage || null)
  }, [errorMessage, t])

  useEffect(() => {
    if (value instanceof File) {
      if (!value.type.startsWith('image/')) {
        setPreview('preview')
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => setPreview(reader.result)
      reader.readAsDataURL(value)

      return
    }

    setPreview(value)
  }, [value])

  const onFileContext = useCallback(() => {
    fileRef.current?.click()
  }, [fileRef])

  const onChange = useCallback(() => {
    const file = fileRef.current?.files[0]

    if (!file) {
      return
    }

    setFieldError?.(name, undefined)
    setFieldValue(name, file)
  }, [setFieldError, setFieldValue, name])

  if (!!preview) {
    return (
      <>
        <div>
          <div
            className={`${styles.upload} ${styles.uploadUploaded} ${
              !!errorText ? styles.uploadWithError : ''
            } ${type === UPLOAD_TYPES.BIG && styles.uploadBig} ${
              type === UPLOAD_TYPES.SMALL && styles.uploadSmall
            }`}
            onClick={onRemove}
          >
            <div className={styles.uploadWrapper}>
              <div className={styles.uploadIcon}>
                <CloseIcon />
              </div>
              <div className={styles.uploadLink}>{removeLabel}</div>
            </div>
            <div className={styles.uploadImage}>
              <Image
                width={100}
                height={100}
                unoptimized
                src={preview}
                objectFit={'cover'}
                alt="Uploaded image"
              />
            </div>
          </div>
          {errorText && (
            <div className={styles.uploadErrorWrapper}>
              <Error message={errorText} />
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={`${styles.upload} ${!!errorText ? styles.uploadWithError : ''} ${
          type === UPLOAD_TYPES.BIG && styles.uploadBig
        } ${type === UPLOAD_TYPES.SMALL && styles.uploadSmall}`}
        onClick={onFileContext}
      >
        <div className={styles.uploadWrapper}>
          <div className={styles.uploadIcon}>
            <AddIcon />
          </div>
          <div className={styles.uploadLink}>{label}</div>
          <input
            className={styles.uploadFile}
            ref={fileRef}
            type="file"
            onChange={onChange}
            name={name}
            onBlur={onBlur}
            {...options}
          />
        </div>
      </div>
      {errorText && (
        <div className={styles.uploadErrorWrapper}>
          <Error message={errorText} />
        </div>
      )}
    </>
  )
}

Upload.propTypes = {}

export default memo(Upload)
